/**
 * What a SAVED payment method can still change, per processor (PD-16214).
 *
 * Once a card is vaulted, which of its fields can be edited depends on the
 * gateway, not on our data model:
 *
 * - CyberSource, Authorize.Net and the mock partner accept a new expiration
 *   date on the stored token, so the edit form may offer it.
 * - Adyen and NMI keep the card data immutable. Adyen holds no mutable
 *   billing profile at all (cardholder name and billing address live only on
 *   our side), and NMI's Customer Vault update takes the billing profile but
 *   not the card. Editing the name and address is still a complete, correct
 *   operation for both; only the expiration date is off limits, because a
 *   value the processor never sees would silently disagree with the token it
 *   charges against, and could hide a card-expiring warning without the card
 *   actually being renewed.
 *
 * Keyed by `PaymentProcessorConfigEntity.processorType`. Anything not listed
 * is treated as not editable, so a new gateway has to opt in deliberately.
 */
const PROCESSORS_WITH_UPDATABLE_EXPIRATION: ReadonlySet<string> = new Set([
	'cybersource',
	'authorize_net',
	'mock',
]);

const PROCESSORS_SUPPORTING_EDIT: ReadonlySet<string> = new Set([
	...PROCESSORS_WITH_UPDATABLE_EXPIRATION,
	'adyen',
	'nmi',
]);

/**
 * Whether a saved payment method on this processor can be edited at all
 * (cardholder name and billing address always; expiration date only where
 * `supportsExpirationDateUpdate` allows).
 */
export const supportsPaymentMethodEdit = (processorType: string): boolean =>
	PROCESSORS_SUPPORTING_EDIT.has(processorType);

/**
 * Whether the processor will take a new expiration date onto the stored
 * token. Where it will not, the edit form shows the date for reference only.
 */
export const supportsExpirationDateUpdate = (processorType: string): boolean =>
	PROCESSORS_WITH_UPDATABLE_EXPIRATION.has(processorType);

/**
 * PD-17281 — processors that can still charge a card used for a hold when the
 * customer chose NOT to save it.
 *
 * A card order is held at checkout and collected later, when it is invoiced:
 * the hold is captured, an invoice above the hold is topped up with a new
 * sale on the same card, and a backorder places a new hold. Only the first
 * of those works from the hold alone. CyberSource is asked to keep a
 * reusable token for the card in the request that places the hold, and that
 * token is kept on the hold's transaction for the payment job to use, so an
 * unsaved card there is as collectible as a saved one.
 *
 * Every other processor would be left with nothing but the hold, so a
 * checkout on those must not offer "don't save this card": the order could
 * never be fully collected. The in-process mock partner is left out for the
 * same reason; it returns no reusable token. Anything not listed is treated
 * as unable, so a new gateway has to opt in deliberately.
 */
const PROCESSORS_CHARGING_UNSAVED_CARDS_LATER: ReadonlySet<string> = new Set([
	'cybersource',
]);

/**
 * Whether a card entered at checkout may be used for the order without being
 * saved, because the processor can still charge it after the hold.
 */
export const chargesUnsavedCardLater = (processorType: string): boolean =>
	PROCESSORS_CHARGING_UNSAVED_CARDS_LATER.has(processorType);
